/**
 * Bookd - Profile Preview Component
 * Shows Bookees how their profile appears to Bookers
 */

import React from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Separator } from './ui/separator';
import { 
  Flame, MapPin, BadgeCheck, ShieldCheck, Calendar, 
  DollarSign, ExternalLink, Image, Play, Eye, EyeOff,
  Star, MessageSquare
} from 'lucide-react';

const ProfilePreview = ({ profile, categoryDetails = {} }) => {
  const { user } = useAuth();
  
  // Use provided profile or fall back to user context
  const profileData = profile || user;
  
  if (!profileData) {
    return (
      <Card className="border-dashed">
        <CardContent className="p-8 text-center text-muted-foreground">
          <Eye className="h-8 w-8 mx-auto mb-3 opacity-50" />
          <p>Complete your profile to see a preview</p>
        </CardContent>
      </Card>
    );
  }

  const coverImage = profileData.portfolio_images?.[0] || 
    'https://images.pexels.com/photos/1763069/pexels-photo-1763069.jpeg';
  
  const displayName = profileData.screen_name || profileData.name || 'Your Name';
  const mainCategory = profileData.main_category || profileData.categories?.[0] || 'Service Category';
  const catDetails = categoryDetails[mainCategory] || {};

  // Calculate display rate
  let displayRate = null;
  if (catDetails.rate_type === 'hourly' && catDetails.hourly_rate) {
    displayRate = `$${catDetails.hourly_rate}/hr`;
  } else if (catDetails.rate_type === 'flat' && catDetails.flat_rate) {
    displayRate = `From $${catDetails.flat_rate}`;
  } else if (profileData.hourly_rate) {
    displayRate = `$${profileData.hourly_rate}/hr`;
  }

  return (
    <Card className="overflow-hidden border-primary/20">
      <CardHeader className="p-0 relative">
        {/* Cover Image */}
        <div className="h-40 relative overflow-hidden">
          <img 
            src={coverImage} 
            alt="Cover" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
          
          {/* Preview Badge */}
          <div className="absolute top-3 right-3">
            <Badge variant="secondary" className="bg-background/80 backdrop-blur-sm">
              <Eye className="h-3 w-3 mr-1" />
              Preview Mode
            </Badge>
          </div>
          
          {/* Avatar */}
          <div className="absolute -bottom-10 left-6">
            <Avatar className="h-20 w-20 border-4 border-background shadow-lg">
              <AvatarImage src={profileData.picture} />
              <AvatarFallback className="text-2xl bg-primary">
                {displayName[0]?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-12 pb-6 px-6">
        {/* Name and Badges */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold flex items-center gap-2">
              {displayName}
              {profileData.is_verified && (
                <BadgeCheck className="h-5 w-5 text-blue-500" title="ID Verified" />
              )}
              {profileData.is_insured && (
                <ShieldCheck className="h-5 w-5 text-green-500" title="Insured" />
              )}
            </h3>
            <Badge variant="secondary" className="mt-1">
              {mainCategory}
            </Badge>
          </div>
          
          {/* Rating */}
          <div className="flex items-center gap-1 text-sm">
            <Flame className="h-4 w-4 text-orange-500 fill-current" />
            <span className="font-medium">{profileData.rating?.toFixed(1) || '0.0'}</span>
            <span className="text-muted-foreground">
              ({profileData.review_count || 0} reviews)
            </span>
          </div>
        </div>

        {/* Location & Rate */}
        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
          {profileData.location && (
            <div className="flex items-center gap-1">
              <MapPin className="h-4 w-4" />
              <span>{profileData.location}</span>
            </div>
          )}
          {displayRate && (
            <div className="flex items-center gap-1 text-primary font-medium">
              <DollarSign className="h-4 w-4" />
              <span>{displayRate}</span>
            </div>
          )}
        </div>

        {/* Bio */}
        {profileData.bio ? (
          <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
            {profileData.bio}
          </p>
        ) : (
          <p className="text-sm text-muted-foreground/50 italic mb-4">
            Add a bio to tell Bookers about yourself...
          </p>
        )}

        {/* Category Description */}
        {catDetails.description && (
          <div className="p-3 rounded-lg bg-muted/50 mb-4">
            <p className="text-xs text-muted-foreground">{catDetails.description}</p>
          </div>
        )}

        <Separator className="my-4" />

        {/* Portfolio Preview */}
        <div className="mb-4">
          <p className="text-xs font-medium text-muted-foreground mb-2">Portfolio</p>
          <div className="flex gap-2">
            {profileData.portfolio_images?.slice(0, 3).map((img, idx) => (
              <div 
                key={idx} 
                className="w-16 h-16 rounded-lg overflow-hidden bg-muted"
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
            {(!profileData.portfolio_images || profileData.portfolio_images.length === 0) && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground/50">
                <Image className="h-4 w-4" />
                <span>Add portfolio images...</span>
              </div>
            )}
            {profileData.portfolio_videos?.length > 0 && (
              <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center">
                <Play className="h-6 w-6 text-muted-foreground" />
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons (Disabled in Preview) */}
        <div className="flex gap-2">
          <Button disabled className="flex-1 rounded-xl opacity-50">
            <MessageSquare className="h-4 w-4 mr-2" />
            Message
          </Button>
          <Button disabled className="flex-1 rounded-xl btn-glow opacity-50">
            <Calendar className="h-4 w-4 mr-2" />
            Book Now
          </Button>
        </div>

        {/* Profile Completeness Tips */}
        <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
          <p className="text-xs font-medium text-amber-600 mb-2">
            <Star className="h-3 w-3 inline mr-1" />
            Profile Tips
          </p>
          <ul className="text-xs text-muted-foreground space-y-1">
            {!profileData.bio && (
              <li>• Add a bio to introduce yourself</li>
            )}
            {(!profileData.portfolio_images || profileData.portfolio_images.length < 3) && (
              <li>• Add at least 3 portfolio images</li>
            )}
            {!profileData.is_verified && (
              <li>• Verify your ID for a trust badge</li>
            )}
            {!profileData.is_insured && (
              <li>• Get insured to attract more Bookers</li>
            )}
            {!catDetails.description && (
              <li>• Add a category description for better search visibility</li>
            )}
            {profileData.bio && profileData.portfolio_images?.length >= 3 && 
             profileData.is_verified && profileData.is_insured && catDetails.description && (
              <li className="text-green-600">✓ Your profile is complete!</li>
            )}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProfilePreview;
